import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import backgroundImage from '../../assets/img/aquine.jpg';
import { BASE_URL } from '../utils/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye, faEyeSlash, faCheckCircle, faTimesCircle, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const ResetPassword = () => {
    const { token } = useParams();
    const navigate = useNavigate();

    const [status, setStatus] = useState('validating'); // 'validating' | 'valid' | 'invalid' | 'success'
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Validar token al montar
    useEffect(() => {
        if (!token) {
            setStatus('invalid');
            return;
        }
        axios
            .get(`${BASE_URL}/resetPassword/validate/${token}`)
            .then((res) => {
                if (res.data.valid) setStatus('valid');
                else setStatus('invalid');
            })
            .catch(() => setStatus('invalid'));
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 8) {
            setError('La contraseña debe tener al menos 8 caracteres.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Las contraseñas no coinciden.');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${BASE_URL}/resetPassword`, { token, password });
            setStatus('success');
            // Redirigir al login después de 3 segundos
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            const msg = err.response?.data?.message || 'Error al actualizar la contraseña. Intenta más tarde.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        if (status === 'validating') {
            return (
                <div className="text-center py-12 space-y-4">
                    <svg className="animate-spin h-10 w-10 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    <p className="text-gray-500 text-sm">Verificando enlace...</p>
                </div>
            );
        }

        if (status === 'invalid') {
            return (
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center space-y-4">
                    <FontAwesomeIcon icon={faTimesCircle} className="h-12 w-12 text-red-500 mx-auto" />
                    <h3 className="text-lg font-semibold text-red-800">Enlace inválido o expirado</h3>
                    <p className="text-sm text-red-700">
                        Este enlace ya fue utilizado o ha expirado (duración: 1 hora).<br />
                        Solicita un nuevo enlace de recuperación.
                    </p>
                    <Link
                        to="/forgotPassword"
                        className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors"
                    >
                        Solicitar nuevo enlace
                    </Link>
                </div>
            );
        }

        if (status === 'success') {
            return (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center space-y-4">
                    <FontAwesomeIcon icon={faCheckCircle} className="h-12 w-12 text-green-500 mx-auto" />
                    <h3 className="text-lg font-semibold text-green-800">¡Contraseña actualizada!</h3>
                    <p className="text-sm text-green-700">
                        Tu contraseña fue restablecida correctamente.<br />
                        Serás redirigido al inicio de sesión en unos segundos.
                    </p>
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Ir al inicio de sesión
                    </Link>
                </div>
            );
        }

        // status === 'valid': mostrar formulario
        return (
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                        {error}
                    </div>
                )}

                {/* Nueva contraseña */}
                <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                        Nueva contraseña
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faLock} className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            id="password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input-field pl-10 pr-10"
                            placeholder="Mínimo 8 caracteres"
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            <FontAwesomeIcon
                                icon={showPassword ? faEyeSlash : faEye}
                                className="h-5 w-5 text-gray-400 hover:text-gray-600"
                            />
                        </button>
                    </div>
                    {password && password.length < 8 && (
                        <p className="mt-1 text-xs text-red-500">Mínimo 8 caracteres</p>
                    )}
                </div>

                {/* Confirmar contraseña */}
                <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                        Confirmar contraseña
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faLock} className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirm ? 'text' : 'password'}
                            required
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="input-field pl-10 pr-10"
                            placeholder="Repite la contraseña"
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowConfirm(!showConfirm)}
                        >
                            <FontAwesomeIcon
                                icon={showConfirm ? faEyeSlash : faEye}
                                className="h-5 w-5 text-gray-400 hover:text-gray-600"
                            />
                        </button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                        <p className="mt-1 text-xs text-red-500">Las contraseñas no coinciden</p>
                    )}
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white gradient-bg hover:shadow-lg transform transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                >
                    {loading ? (
                        <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                            </svg>
                            Guardando...
                        </span>
                    ) : (
                        'Restablecer contraseña'
                    )}
                </button>

                <div className="text-center">
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Volver al inicio de sesión
                    </Link>
                </div>
            </form>
        );
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image */}
            <div
                className="hidden lg:flex lg:w-3/5 relative bg-cover bg-center"
                style={{ backgroundImage: `url(${backgroundImage})` }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-primary-900/70 to-primary-600/70"></div>
                <div className="relative z-10 flex flex-col justify-center items-center text-white p-12">
                    <h1 className="text-5xl font-bold mb-4">Nueva contraseña</h1>
                    <p className="text-xl text-center max-w-md">Sistema de Control de Acceso</p>
                </div>
            </div>

            {/* Right side */}
            <div className="flex-1 flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-700 shadow-lg">
                            <FontAwesomeIcon icon={faLock} className="h-8 w-8 text-white" />
                        </div>
                        <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                            Restablecer contraseña
                        </h2>
                        {status === 'valid' && (
                            <p className="mt-2 text-sm text-gray-600">
                                Ingresa tu nueva contraseña
                            </p>
                        )}
                    </div>

                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
